import React, { useState, useRef, useEffect } from "react";
import "./AddWorkerModal.css";
import { X, Upload, Plus, Trash2, Eye, FileText, Download, CheckSquare, Square } from "lucide-react";
import { useTranslation } from "react-i18next"; // Pridėtas importas
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from 'xlsx';

export default function AddWorkerModal({ initialData, companyInfo, onClose, onSave }) {
  const { t } = useTranslation(); // Inicijuojame vertimus

  const formatDateForPicker = (dateObj) => {
    if (!dateObj) return new Date().toISOString().split('T')[0];
    const d = dateObj.seconds ? new Date(dateObj.seconds * 1000) : new Date(dateObj);
    return d.toISOString().split('T')[0];
  };

  const [name, setName] = useState(initialData?.name || "");
  const [email, setEmail] = useState(initialData?.email || "");
  const [phone, setPhone] = useState(initialData?.phone || "");
  const [entryDate, setEntryDate] = useState(formatDateForPicker(initialData?.createdAt));

  const [customFields, setCustomFields] = useState(initialData?.customFields || []);
  const [existingDocuments, setExistingDocuments] = useState(initialData?.documents || []);
  
  const [isExportMode, setIsExportMode] = useState(false);
  
  const [selectedFields, setSelectedFields] = useState({ name: true, email: true, phone: true });
  const [selectedCustomFields, setSelectedCustomFields] = useState(initialData?.customFields?.map(() => true) || []);
  const [selectedDocs, setSelectedDocs] = useState(initialData?.documents?.map(() => true) || []);

  const fileInputRef = useRef(null);

  const displayAddress = () => {
    const addr = companyInfo?.address;
    if (!addr) return "";
    return typeof addr === 'object' ? `${addr.street || ""}, ${addr.zip || ""} ${addr.city || ""}`.trim() : addr;
  };

  const handleNameChange = (e) => {
    const val = e.target.value;
    if (val.length === 1) {
        setName(val.toUpperCase());
    } else {
        setName(val);
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
        const file = e.target.files[0];
        const newDoc = {
            name: file.name,
            url: URL.createObjectURL(file),
            file: file 
        };
        setExistingDocuments([...existingDocuments, newDoc]);
        e.target.value = null; 
    }
  };

  const handleExcelExport = () => {
      // Pakeista į vertimus stulpelių pavadinimuose
      const rowData = {
          [t('name')]: name,
          "E-Mail": email,
          [t('phone')]: phone,
          [t('created_at')]: new Date(entryDate).toLocaleDateString()
      };

      customFields.forEach(f => {
          if (f.label) rowData[f.label] = f.value || "";
      });

      const ws = XLSX.utils.json_to_sheet([rowData]);
      const wscols = [{wch: 25}, {wch: 30}, {wch: 20}, {wch: 15}];
      customFields.forEach(() => wscols.push({wch: 20}));
      ws['!cols'] = wscols;

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Info");
      XLSX.writeFile(wb, `${name || "Worker"}_export.xlsx`);
  };

  const generatePDF = () => {
    const doc = new jsPDF();
    const address = displayAddress();
    
    doc.setFontSize(22);
    doc.setTextColor(40, 40, 40);
    doc.text(companyInfo?.name?.toUpperCase() || "BUILD-FORCE INFO", 14, 22);
    
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(`${t('address').toUpperCase()}: ${address}`, 14, 30);
    doc.text(`${t('date').toUpperCase()}: ${new Date().toLocaleDateString()}`, 14, 35);
    
    doc.setDrawColor(200, 200, 200);
    doc.line(14, 40, 196, 40);

    const body = [];
    if (selectedFields.name) body.push([t('name').toUpperCase(), name]);
    if (selectedFields.email) body.push(["E-MAIL", email]);
    if (selectedFields.phone) body.push([t('phone').toUpperCase(), phone]);
    body.push([t('created_at').toUpperCase(), new Date(entryDate).toLocaleDateString()]);
    
    customFields.forEach((f, i) => { 
        if (selectedCustomFields[i]) body.push([f.label.toUpperCase(), f.value]); 
    });

    autoTable(doc, { 
        startY: 45, 
        head: [[t('category').toUpperCase(), t('info').toUpperCase()]], 
        body: body, 
        theme: 'grid', 
        headStyles: { fillColor: [41, 128, 185], textColor: [255, 255, 255] },
        styles: { fontSize: 11, cellPadding: 6 }
    });

    const chosenDocs = existingDocuments.filter((_, i) => selectedDocs[i]);
    if (chosenDocs.length > 0) {
      let y = doc.lastAutoTable.finalY + 15;
      doc.setFontSize(14);
      doc.setTextColor(40, 40, 40);
      doc.text(t('documents').toUpperCase() + ":", 14, y);
      
      doc.setFontSize(10);
      doc.setTextColor(0, 102, 204); 
      chosenDocs.forEach((d, i) => {
        const text = `• ${d.name}`;
        const yPos = y + 8 + (i * 7);
        if (d.url) { doc.textWithLink(text, 14, yPos, { url: d.url }); } 
        else { doc.text(text, 14, yPos); }
      });
    }
    doc.save(`${name || 'Worker'}_export.pdf`);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        
        {/* HEADER */}
        <div className="modal-header-container">
          <h2 className="header-title-blue">
            {isExportMode ? t('export_mode') : (initialData ? t('edit') : t('info'))}
          </h2>
          
          {!isExportMode && (
            <button className="header-action-btn" onClick={() => setIsExportMode(true)}>
                <FileText size={16} /> <span>{t('export')}</span>
            </button>
          )}

          <button type="button" className="close-btn" onClick={onClose}><X size={24} /></button>
        </div>

        {/* BODY */}
        <div className="modal-body">
          <div className="company-info-display-box">
            <div className="comp-name-main">{companyInfo?.name}</div>
            <div className="comp-address-sub">{displayAddress()}</div>
          </div>

          <div className="date-selection-row">
            <label className="date-label">{t('created_at').toUpperCase()}:</label>
            <input type="date" className="date-picker-input" value={entryDate} onChange={e => setEntryDate(e.target.value)} disabled={isExportMode} />
          </div>

          <div className="input-group-main">
            <div className="label-row-spacing">
              {isExportMode && (
                  <div className="sel-box" onClick={() => setSelectedFields({...selectedFields, name: !selectedFields.name})}>
                    {selectedFields.name ? <CheckSquare size={18} color="#ffd700" /> : <Square size={18} color="#555" />}
                  </div>
              )}
              <label onClick={() => isExportMode && setSelectedFields({...selectedFields, name: !selectedFields.name})}>
                {t('name').toUpperCase()}
              </label>
            </div>
            <input type="text" value={name} onChange={handleNameChange} disabled={isExportMode} />
          </div>

          <div className="two-column-grid">
            <div className="input-group-main">
              <div className="label-row-spacing">
                {isExportMode && (
                    <div className="sel-box" onClick={() => setSelectedFields({...selectedFields, email: !selectedFields.email})}>
                        {selectedFields.email ? <CheckSquare size={18} color="#ffd700" /> : <Square size={18} color="#555" />}
                    </div>
                )}
                <label onClick={() => isExportMode && setSelectedFields({...selectedFields, email: !selectedFields.email})}>E-MAIL</label>
              </div>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} disabled={isExportMode} />
            </div>

            <div className="input-group-main">
              <div className="label-row-spacing">
                {isExportMode && (
                    <div className="sel-box" onClick={() => setSelectedFields({...selectedFields, phone: !selectedFields.phone})}>
                        {selectedFields.phone ? <CheckSquare size={18} color="#ffd700" /> : <Square size={18} color="#555" />}
                    </div>
                )}
                <label onClick={() => isExportMode && setSelectedFields({...selectedFields, phone: !selectedFields.phone})}>{t('phone').toUpperCase()}</label>
              </div>
              <input type="text" value={phone} onChange={e => setPhone(e.target.value)} disabled={isExportMode} />
            </div>
          </div>

          <hr className="divider-line" />

          <div className="extra-info-section">
            <label className="section-title-tag" style={{color: '#fff', fontSize:'11px', fontWeight:'bold', marginBottom:'10px', display:'block'}}>
                {t('additional_info').toUpperCase()}
            </label>
            <div className="dynamic-fields-list">
              {customFields.map((field, index) => (
                <div key={index} className="field-grid-row">
                  <input type="text" value={field.label} disabled={isExportMode} placeholder={t('category')} onChange={e => {
                    const up = [...customFields]; up[index].label = e.target.value; setCustomFields(up);
                  }} />
                  <input type="text" value={field.value} disabled={isExportMode} placeholder={t('info')} onChange={e => {
                    const up = [...customFields]; up[index].value = e.target.value; setCustomFields(up);
                  }} />
                  
                  {isExportMode ? (
                    <div className="sel-box" style={{justifyContent:'center'}} onClick={() => {
                        const up = [...selectedCustomFields]; up[index] = !up[index]; setSelectedCustomFields(up);
                    }}>
                      {selectedCustomFields[index] ? <CheckSquare size={20} color="#ffd700" /> : <Square size={20} color="#555" />}
                    </div>
                  ) : (
                    <button className="delete-btn-red" onClick={() => setCustomFields(customFields.filter((_, i) => i !== index))}><Trash2 size={18}/></button>
                  )}
                </div>
              ))}
            </div>
            {!isExportMode && <button type="button" className="add-field-btn-modern" onClick={() => setCustomFields([...customFields, { label: "", value: "" }])}>
              <Plus size={16} /> {t('add_field').toUpperCase()}
            </button>}
          </div>

          <hr className="divider-line" />

          <div className="file-upload-section">
            <label className="section-title-tag doc-label-margin" style={{color: '#fff', fontSize:'11px', fontWeight:'bold', marginBottom:'10px', display:'block'}}>
                {t('documents').toUpperCase()}
            </label>
            <div className="docs-list-scroll">
              {existingDocuments.map((doc, i) => (
                <div key={i} className="doc-item-card">
                  <div className="doc-left-group">
                    {isExportMode && (
                      <div className="sel-box" onClick={() => {
                        const up = [...selectedDocs]; up[i] = !up[i]; setSelectedDocs(up);
                      }}>
                        {selectedDocs[i] ? <CheckSquare size={18} color="#ffd700" /> : <Square size={18} color="#555" />}
                      </div>
                    )}
                    <button className="eye-btn-scoped" onClick={() => window.open(doc.url, '_blank')}><Eye size={18} /></button>
                  </div>
                  <input type="text" className="doc-name-edit-input" value={doc.name} disabled={isExportMode} onChange={e => {
                    const up = [...existingDocuments]; up[i].name = e.target.value; setExistingDocuments(up);
                  }} />
                  {!isExportMode && <button className="delete-btn-red-small" onClick={() => setExistingDocuments(existingDocuments.filter((_, idx) => idx !== i))}><Trash2 size={18} /></button>}
                </div>
              ))}
            </div>
            
            {!isExportMode && (
              <>
                <input 
                  type="file" 
                  ref={fileInputRef} 
                  style={{ display: "none" }} 
                  onChange={handleFileSelect}
                />
                <button type="button" className="upload-btn-action-green" onClick={() => fileInputRef.current.click()}>
                  <Upload size={18} /> {t('upload').toUpperCase()}
                </button>
              </>
            )}
          </div>
        </div>

        {/* FOOTER */}
        <div className="modal-footer-container">
          {!isExportMode ? (
            <>
              <button type="button" className="footer-btn-cancel" onClick={onClose}>{t('cancel')}</button>
              <button type="button" className="footer-btn-save" onClick={() => {
                  const newFilesOnly = existingDocuments.filter(d => d.file).map(d => ({file: d.file, customName: d.name}));
                  const oldDocs = existingDocuments.filter(d => !d.file);
                  
                  onSave(initialData?.id, {
                      name, email, phone, customFields, documents: oldDocs, newFiles: newFilesOnly, createdAt: new Date(entryDate)
                  });
              }}>{t('save').toUpperCase()}</button>
            </>
          ) : (
            <div className="export-controls-bar">
              <button className="footer-action-btn style-back" onClick={() => setIsExportMode(false)}>{t('back').toUpperCase()}</button>
              <button className="footer-action-btn style-pdf" onClick={generatePDF}><Download size={18}/> PDF EXPORT</button>
              <button type="button" className="footer-action-btn style-excel" onClick={handleExcelExport}><Download size={18}/> EXCEL</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}